import React, { useEffect, useState } from 'react';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import { FaPlus, FaLaptop, FaRegFileAlt, FaTags } from 'react-icons/fa';
import { Link } from 'react-router-dom';

const API_INSTALLATION = 'https://dev.sliitleo.org/Backend/installation_reg.php';

/* ---------- helpers ---------- */

// Accept array, {all}, {rows}, {data}, {installations}. Fall back to [].
function pickInstallations(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload?.all && Array.isArray(payload.all)) return payload.all;
  if (payload?.rows && Array.isArray(payload.rows)) return payload.rows;
  if (payload?.data && Array.isArray(payload.data)) return payload.data;
  if (payload?.installations && Array.isArray(payload.installations)) return payload.installations;
  return [];
}

// Safe JSON parsing so HTML error pages don’t crash the app
async function toJsonSafe(res) {
  const text = await res.text();
  try { return JSON.parse(text); }
  catch {
    console.error('Invalid JSON from API:', text.slice(0, 800));
    throw new Error('Invalid JSON from server');
  }
}

// Normalize bank field across payloads
function getBank(item) {
  return (
    item.BankName ??
    item.Bank ??
    item.bank ??
    item.Bankname ??
    'Not Specified'
  ) || 'Not Specified';
}

// Normalize amount strings like "Rs. 1,500.00" to number
function parseAmount(v) {
  const n = parseFloat(String(v ?? '').replace(/[^\d.]/g, ''));
  return Number.isFinite(n) ? n : 0;
}

export default function Installation() {
  // user first – referenced in handlers
  const user = JSON.parse(localStorage.getItem('user')) || {
    SLIITID: '',
    name: 'Unknown User',
    role: 'Member',
    image: '/user.png'
  };

  // State
  const [verifyPopup, setVerifyPopup] = useState({ open: false, regid: null });
  const [installations, setInstallations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  // Verify handlers (kept as-is)
  const handleVerify = (regid) => setVerifyPopup({ open: true, regid });
  const handleVerifyCancel = () => setVerifyPopup({ open: false, regid: null });

  const handleVerifyAccept = async () => {
    const regid = verifyPopup.regid;
    setVerifyPopup({ open: false, regid: null });
    try {
      const res = await fetch(API_INSTALLATION, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: `action=verify&RegID=${encodeURIComponent(regid)}&Verify=1&VerifiedBy=${encodeURIComponent(user.SLIITID)}`
      });
      const data = await toJsonSafe(res);
      if (data.success) {
        setInstallations(list =>
          list.map(item =>
            item.RegID === regid
              ? { ...item, Verify: 1, VerifiedBy: user.SLIITID, VerifiedDate: data.VerifiedDate }
              : item
          )
        );
      } else {
        alert('Failed to verify registration');
      }
    } catch {
      alert('Error verifying registration');
    }
  };

  // Check-in (kept as-is)
  const handleCheckIn = async (regid, currentStatus) => {
    if (currentStatus === 1 || currentStatus === '1' || currentStatus === true) return;
    try {
      const res = await fetch(API_INSTALLATION, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: `action=checkin&RegID=${encodeURIComponent(regid)}&CheckIn=1&CheckedBy=${encodeURIComponent(user.SLIITID)}`
      });
      const data = await toJsonSafe(res);
      if (data.success) {
        setInstallations(list =>
          list.map(item =>
            item.RegID === regid
              ? { ...item, CheckIn: 1, CheckedBy: user.SLIITID, CheckerName: user.name }
              : item
          )
        );
      } else {
        alert('Failed to update check-in status: ' + (data.message || 'Please try again'));
      }
    } catch {
      alert('An error occurred while processing your request');
    }
  };

  // Load
  const load = async () => {
    setErr('');
    setLoading(true);
    try {
      const res = await fetch(API_INSTALLATION, { method: 'GET' });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const payload = await toJsonSafe(res);
      setInstallations(pickInstallations(payload));
    } catch (e) {
      console.error('Fetch error:', e);
      setInstallations([]);
      setErr('Could not load registrations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // ---- Stats (robust) ----
  const totalMembers = installations.length;

  const totalCheckins = installations.reduce((acc, i) =>
    acc + ((i.CheckIn === 1 || i.CheckIn === '1' || i.CheckIn === true) ? 1 : 0)
  , 0);

  const myCheckins = installations.reduce((acc, i) =>
    acc + ((i.CheckedBy === user.SLIITID) && (i.CheckIn === 1 || i.CheckIn === '1' || i.CheckIn === true) ? 1 : 0)
  , 0);

  // Food stats
  const foodTally = installations.reduce((acc, item) => {
    const raw = String(item.FoodPref || '').trim().toLowerCase();
    if (/^veg(?:etarian)?$/.test(raw) || raw === 'vegan') acc.vegetarians++;
    else if (/^non[\s-]?veg(?:etarian)?$/.test(raw) || raw === 'non') acc.nonVegetarians++;
    else if (raw) acc.other++;
    else acc.unspecified++;
    return acc;
  }, { vegetarians: 0, nonVegetarians: 0, other: 0, unspecified: 0 });

  // Bank stats
  const bankBuckets = installations.reduce((acc, item) => {
    const bank = getBank(item);
    const amt = parseAmount(item.Amount);
    if (!acc[bank]) acc[bank] = { count: 0, totalAmount: 0 };
    acc[bank].count += 1;
    acc[bank].totalAmount += amt;
    return acc;
  }, {});
  const bankTransactions = Object.entries(bankBuckets).filter(([b]) => b !== 'Not Specified').reduce((n, [,v]) => n + v.count, 0);
  const totalAmount = Object.values(bankBuckets).reduce((sum, v) => sum + v.totalAmount, 0);
  const avgAmount = bankTransactions > 0 ? totalAmount / bankTransactions : 0;

  const estimatedTime = '0h 6m'; // your placeholder

  // UI
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen text-white bg-[#111827]">
        Loading installation registrations...
      </div>
    );
  }

  return (
    <>
      {/* Verify popup */}
      {verifyPopup.open && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center', background: 'rgba(16,16,24,0.25)' }}>
          <div className="bg-white/10 border border-white/20 shadow-2xl rounded-2xl p-8 flex flex-col items-center relative animate-fade-in" style={{ minWidth: 340, maxWidth: 380 }}>
            <button onClick={handleVerifyCancel} className="absolute top-3 right-3 text-gray-300 hover:text-red-400 text-xl font-bold rounded-full p-1 transition" aria-label="Close">
              &times;
            </button>
            <div className="flex items-center justify-center w-14 h-14 rounded-full bg-green-100/80 mb-3 shadow-md">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
            </div>
            <div className="font-bold text-lg text-white mb-1 drop-shadow">Verify Registration</div>
            <div className="mb-4 text-sm text-gray-200 text-center">Are you sure you want to <span className="text-green-400 font-semibold">verify</span> this registration?</div>
            <div className="flex gap-4 mt-2">
              <button onClick={handleVerifyAccept} className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 px-6 py-2 rounded-lg font-bold text-white shadow-lg transition-all text-sm flex items-center gap-1">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                Accept
              </button>
              <button onClick={handleVerifyCancel} className="bg-white/10 border border-gray-400 hover:bg-gray-100 hover:text-gray-800 px-6 py-2 rounded-lg font-bold text-gray-200 hover:shadow-lg transition-all text-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div className="flex bg-[#111827] text-white min-h-screen">
        <Sidebar />
        <Topbar user={user} />
        <main className="lg:ml-64 mt-14 w-full px-4 sm:px-6 py-6 sm:py-8">
          {/* Header */}
          <div className="backdrop-blur-md bg-gradient-to-r from-[#23272f]/80 to-[#181b20]/80 border border-gray-800 rounded-2xl p-4 sm:p-5 mb-6 sm:mb-8 flex items-center gap-3 sm:gap-4 shadow-lg">
            <span className="flex items-center gap-2 sm:gap-3">
              <span className="bg-indigo-600 p-1.5 sm:p-2 rounded-full shadow-lg flex items-center justify-center">
                <FaLaptop className="text-white text-xl sm:text-2xl" />
              </span>
              <span className="text-base sm:text-lg font-bold tracking-wide text-white drop-shadow">
                Installation <span className="text-gray-400 font-normal hidden sm:inline">/ Registration</span>
              </span>
            </span>
          </div>

          {/* Top buttons */}
          <div className="flex flex-wrap gap-3 sm:gap-4 mb-6 sm:mb-8">
            <Link to="/installation" className="bg-[#23272f] px-3 sm:px-5 py-1.5 sm:py-2 rounded-lg sm:rounded-xl font-medium sm:font-semibold flex items-center gap-1.5 sm:gap-2 text-sm sm:text-base text-white border border-gray-700 shadow hover:scale-105 transition flex-1 sm:flex-none justify-center">
              <FaRegFileAlt className="text-blue-400 text-sm sm:text-base" />
              <span>Registration</span>
            </Link>
            <Link to="/add-registration" className="bg-[#23272f] px-3 sm:px-5 py-1.5 sm:py-2 rounded-lg sm:rounded-xl font-medium sm:font-semibold flex items-center gap-1.5 sm:gap-2 text-sm sm:text-base text-white border border-gray-700 shadow hover:scale-105 transition flex-1 sm:flex-none justify-center">
              <FaPlus className="text-indigo-400 text-sm sm:text-base" />
              <span>Add Registration</span>
            </Link>
            <Link to="/installation-report" className="bg-[#23272f] px-3 sm:px-5 py-1.5 sm:py-2 rounded-lg sm:rounded-xl font-medium sm:font-semibold flex items-center gap-1.5 sm:gap-2 text-sm sm:text-base text-white border border-gray-700 shadow hover:scale-105 transition flex-1 sm:flex-none justify-center">
              <FaTags className="text-indigo-400 text-sm sm:text-base" />
              <span>Summary </span>
            </Link>
          </div>

          {/* Analytics container */}
          <div className="bg-gray-900 rounded-2xl shadow-2xl p-6 w-full max-w-7xl mx-auto border border-gray-700">
            {/* Header Section */}
            <div className="mb-8 flex flex-col md:flex-row justify-between items-start md:items-center">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
                  Registration Analytics
                </h1>
                <p className="text-sm text-gray-400 mt-1">Comprehensive overview of registration data and statistics</p>
              </div>
              <div className="mt-4 md:mt-0 text-sm text-gray-400 bg-gray-800/50 px-3 py-2 rounded-lg">
                📅 Last updated: {new Date().toLocaleString()}
                {err && (
                  <button onClick={load} className="ml-3 px-3 py-1 rounded bg-indigo-600 hover:bg-indigo-700 text-white text-xs">
                    Reload
                  </button>
                )}
              </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
              {/* Total Members */}
              <div className="bg-gradient-to-br from-blue-900/40 to-blue-800/20 border border-purple-800 rounded-xl p-6 shadow-lg hover:shadow-blue-500/10 transition-all duration-300">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-300/90 mb-1">Total Members</p>
                    <p className="text-3xl font-bold text-white mb-3">{totalMembers.toLocaleString()}</p>
                    <div className="flex items-center text-xs space-x-2">
                      <span className="px-2 py-1 bg-green-500/20 text-green-300 rounded-full flex items-center">
                        <span className="w-1.5 h-1.5 bg-green-400 rounded-full mr-1.5"></span>
                        {totalCheckins} checked in
                      </span>
                      <span className="text-gray-400">•</span>
                      <span className="text-gray-400">
                        {Math.max(0, totalMembers - totalCheckins)} pending
                      </span>
                    </div>
                  </div>
                  <div className="p-2.5 bg-blue-500/20 rounded-lg backdrop-blur-sm">
                    <svg className="w-6 h-6 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Food Preferences */}
              <div className="bg-gradient-to-br from-purple-900/40 to-purple-800/20 border border-purple-800 rounded-xl p-6 shadow-lg hover:shadow-purple-500/10 transition-all duration-300">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-purple-300/90 mb-1">Food Preferences</p>
                    <p className="text-3xl font-bold text-white mb-3">
                      {(foodTally.vegetarians + foodTally.nonVegetarians).toLocaleString()}
                    </p>
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <span className="px-2 py-1 bg-green-500/20 text-green-300 rounded-full flex items-center">
                        <span className="w-2 h-2 bg-green-400 rounded-full mr-1.5"></span>
                        {foodTally.vegetarians} Veg
                      </span>
                      <span className="px-2 py-1 bg-red-500/20 text-red-300 rounded-full flex items-center">
                        <span className="w-2 h-2 bg-red-400 rounded-full mr-1.5"></span>
                        {foodTally.nonVegetarians} Non-Veg
                      </span>
                      {foodTally.other > 0 && (
                        <span className="px-2 py-1 bg-yellow-500/20 text-yellow-300 rounded-full flex items-center">
                          <span className="w-2 h-2 bg-yellow-400 rounded-full mr-1.5"></span>
                          {foodTally.other} Other
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="p-2.5 bg-purple-500/20 rounded-lg backdrop-blur-sm">
                    <svg className="w-6 h-6 text-purple-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 6v6m0 0v6m0-6h6M12 12H6" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Bank Transactions */}
              <div className="bg-gradient-to-br from-emerald-900/40 to-emerald-800/20 border border-purple-800 rounded-xl p-6 shadow-lg hover:shadow-emerald-500/10 transition-all duration-300">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-emerald-300/90 mb-1">Bank Transactions</p>
                    <p className="text-3xl font-bold text-white mb-3">{bankTransactions.toLocaleString()}</p>
                    <div className="space-y-1.5">
                      <div className="flex items-center text-sm">
                        <span className="text-emerald-300/80 w-20">Total:</span>
                        <span className="font-medium">LKR {totalAmount.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center text-sm">
                        <span className="text-emerald-300/80 w-20">Avg:</span>
                        <span>LKR {avgAmount.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="p-2.5 bg-emerald-500/20 rounded-lg backdrop-blur-sm">
                    <svg className="w-6 h-6 text-emerald-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Faculties */}
              <div className="bg-gradient-to-br from-amber-900/40 to-amber-800/20 border border-purple-800 rounded-xl p-6 shadow-lg hover:shadow-amber-500/10 transition-all duration-300">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-amber-300/90 mb-1">Faculties</p>
                    <p className="text-3xl font-bold text-white mb-3">
                      {Object.keys(
                        installations.reduce((acc, item) => {
                          const f = item.Faculty || 'Not Specified';
                          acc[f] = true; return acc;
                        }, {})
                      ).length.toLocaleString()}
                    </p>
                    <div className="space-y-1.5">
                      <div className="flex items-center text-sm">
                        <span className="text-amber-300/80 w-20">Members:</span>
                        <span className="font-medium">
                          {installations.length.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex items-center text-sm">
                        <span className="text-amber-300/80 w-20">Largest:</span>
                        <span>
                          {(() => {
                            const facultyCounts = installations.reduce((acc, item) => {
                              const f = item.Faculty || 'Not Specified';
                              acc[f] = (acc[f] || 0) + 1; return acc;
                            }, {});
                            const largest = Object.entries(facultyCounts).sort((a,b)=>b[1]-a[1])[0];
                            return largest ? `${largest[0]} (${largest[1]})` : 'N/A';
                          })()}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="p-2.5 bg-amber-500/20 rounded-lg backdrop-blur-sm">
                    <svg className="w-6 h-6 text-amber-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path d="M12 14l9-5-9-5-9 5 9 5z" />
                      <path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Bank-wise Summary */}
            <div className="bg-gray-900/50 backdrop-blur-sm border border-green-800 rounded-xl p-6 mb-8 shadow-xl">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-white flex items-center">
                    <svg className="w-5 h-5 mr-2 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                    </svg>
                    Bank-wise Summary
                  </h3>
                  <p className="text-xs text-gray-400 mt-1">Financial breakdown by banking institution</p>
                </div>
                <div className="text-xs px-3 py-1 bg-blue-900/30 text-blue-300 rounded-full border border-blue-800/50">
                  {Object.keys(bankBuckets).length} Banks
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-500">
                  <thead>
                    <tr className="text-left text-xs text-gray-400">
                      <th className="pb-3 font-medium">Bank Name</th>
                      <th className="pb-3 font-medium text-right">Members</th>
                      <th className="pb-3 font-medium text-right">Total Amount</th>
                      <th className="pb-3 font-medium text-right">Avg. Per Member</th>
                      <th className="pb-3 font-medium text-right">% of Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800/50">
                    {Object.entries(bankBuckets)
                      .sort((a, b) => b[1].count - a[1].count)
                      .map(([bank, data]) => {
                        const percentage = totalMembers ? (data.count / totalMembers) * 100 : 0;
                        return (
                          <tr key={bank} className="hover:bg-gray-800/30 transition-colors">
                            <td className="py-3 text-sm font-medium text-white">
                              <div className="flex items-center">
                                <div className="h-2 w-2 rounded-full bg-blue-500 mr-2"></div>
                                {bank}
                              </div>
                            </td>
                            <td className="py-3 text-sm text-gray-300 text-right">
                              {data.count}
                              <span className="ml-1 text-xs text-gray-500">({percentage.toFixed(1)}%)</span>
                            </td>
                            <td className="py-3 text-sm font-medium text-right text-blue-400">
                              Rs. {data.totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                            <td className="py-3 text-sm text-right text-gray-300">
                              Rs. {(data.count ? (data.totalAmount / data.count) : 0).toFixed(2)}
                            </td>
                            <td className="py-3 text-right">
                              <div className="inline-flex items-center">
                                <div className="w-20 bg-gray-700 rounded-full h-1.5 mr-2">
                                  <div className="bg-blue-500 h-1.5 rounded-full" style={{ width: `${percentage}%` }} />
                                </div>
                                <span className="text-xs font-medium text-gray-400">{percentage.toFixed(1)}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Faculty and Food sections */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              {/* Faculty Distribution */}
              <div className="bg-gray-900/50 backdrop-blur-sm border border-blue-800 rounded-xl p-6 shadow-xl">
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-white flex items-center">
                    <svg className="w-5 h-5 mr-2 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path d="M12 14l9-5-9-5-9 5 9 5z" />
                      <path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
                    </svg>
                    Faculty Distribution
                  </h3>
                  <p className="text-xs text-gray-400 mt-1">Member distribution across faculties</p>
                </div>
                <div className="space-y-4">
                  {Object.entries(
                    installations.reduce((acc, item) => {
                      const faculty = item.Faculty || 'Not Specified';
                      acc[faculty] = (acc[faculty] || 0) + 1;
                      return acc;
                    }, {})
                  ).sort((a,b)=>b[1]-a[1]).map(([faculty, count]) => {
                    const pct = totalMembers ? (count / totalMembers) * 100 : 0;
                    return (
                      <div key={faculty} className="mb-2">
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-300">{faculty}</span>
                          <span className="font-medium text-white">
                            {count} <span className="text-gray-400 text-xs">({pct.toFixed(1)}%)</span>
                          </span>
                        </div>
                        <div className="w-full bg-gray-700 rounded-full h-2">
                          <div className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Food Preferences */}
              <div className="bg-gray-900/50 backdrop-blur-sm border border-blue-800 rounded-xl p-6 shadow-xl">
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-white flex items-center">
                    <svg className="w-5 h-5 mr-2 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6M12 12H6" />
                    </svg>
                    Food Preferences
                  </h3>
                  <p className="text-xs text-gray-400 mt-1">Dietary preferences of registered members</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Veg */}
                  <div className="bg-gradient-to-br from-green-900/30 to-green-800/20 border border-green-500 p-4 rounded-lg">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm font-medium text-green-300">Vegetarian</p>
                        <p className="text-2xl font-bold text-white mt-1">{foodTally.vegetarians}</p>
                        <p className="text-xs text-green-300 mt-1">
                          {totalMembers ? ((foodTally.vegetarians / totalMembers) * 100).toFixed(1) : '0.0'}% of total
                        </p>
                      </div>
                      <div className="p-2 bg-green-500/20 rounded-lg">
                        <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                        </svg>
                      </div>
                    </div>
                    <div className="mt-3 pt-2 border-t border-green-800/50">
                      <div className="h-1.5 w-full bg-green-900/50 rounded-full overflow-hidden">
                        <div className="h-full bg-green-500 rounded-full" style={{ width: `${totalMembers ? (foodTally.vegetarians / totalMembers) * 100 : 0}%` }} />
                      </div>
                    </div>
                  </div>

                  {/* Non-Veg */}
                  <div className="bg-gradient-to-br from-red-900/30 to-red-800/20 border border-orange-500 p-4 rounded-lg">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm font-medium text-red-300">Non-Vegetarian</p>
                        <p className="text-2xl font-bold text-white mt-1">{foodTally.nonVegetarians}</p>
                        <p className="text-xs text-red-300 mt-1">
                          {totalMembers ? ((foodTally.nonVegetarians / totalMembers) * 100).toFixed(1) : '0.0'}% of total
                        </p>
                      </div>
                      <div className="p-2 bg-red-500/20 rounded-lg">
                        <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      </div>
                    </div>
                    <div className="mt-3 pt-2 border-t border-red-800/50">
                      <div className="h-1.5 w-full bg-red-900/50 rounded-full overflow-hidden">
                        <div className="h-full bg-red-500 rounded-full" style={{ width: `${totalMembers ? (foodTally.nonVegetarians / totalMembers) * 100 : 0}%` }} />
                      </div>
                    </div>
                  </div>

                  {/* Other */}
                  {foodTally.other > 0 && (
                    <div className="bg-gradient-to-br from-yellow-900/30 to-yellow-800/20 border border-yellow-800/50 p-4 rounded-lg">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-sm font-medium text-yellow-300">Other</p>
                          <p className="text-2xl font-bold text-white mt-1">{foodTally.other}</p>
                          <p className="text-xs text-yellow-300 mt-1">
                            {totalMembers ? ((foodTally.other / totalMembers) * 100).toFixed(1) : '0.0'}% of total
                          </p>
                        </div>
                        <div className="p-2 bg-yellow-500/20 rounded-lg">
                          <svg className="w-6 h-6 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.933 12.8a1 1 0 000-1.6L6.6 7.2A1 1 0 005 8v8a1 1 0 001.6.8l5.333-4zM19.933 12.8a1 1 0 000-1.6l-5.333-4A1 1 0 0013 8v8a1 1 0 001.6.8l5.333-4z" />
                          </svg>
                        </div>
                      </div>
                      <div className="mt-3 pt-2 border-t border-yellow-800/50">
                        <div className="h-1.5 w-full bg-yellow-900/50 rounded-full overflow-hidden">
                          <div className="h-full bg-yellow-500 rounded-full" style={{ width: `${totalMembers ? (foodTally.other / totalMembers) * 100 : 0}%` }} />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Unspecified */}
                  {foodTally.unspecified > 0 && (
                    <div className="bg-gradient-to-br from-gray-700/30 to-gray-800/20 border border-gray-700/50 p-4 rounded-lg">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-sm font-medium text-gray-300">Not Specified</p>
                          <p className="text-2xl font-bold text-white mt-1">{foodTally.unspecified}</p>
                          <p className="text-xs text-gray-400 mt-1">
                            {totalMembers ? ((foodTally.unspecified / totalMembers) * 100).toFixed(1) : '0.0'}% of total
                          </p>
                        </div>
                        <div className="p-2 bg-gray-500/20 rounded-lg">
                          <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                        </div>
                      </div>
                      <div className="mt-3 pt-2 border-t border-gray-700/50">
                        <div className="h-1.5 w-full bg-gray-700/50 rounded-full overflow-hidden">
                          <div className="h-full bg-gray-500 rounded-full" style={{ width: `${totalMembers ? (foodTally.unspecified / totalMembers) * 100 : 0}%` }} />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* (Optional) You can render a small list and wire verify/check-in here if needed */}
          {/* Example:
            installations.slice(0, 5).map(row => (
              <button onClick={() => handleVerify(row.RegID)}>Verify {row.Name}</button>
            ))
          */}
        </main>
      </div>
    </>
  );
}
